import { Component, EventEmitter, Input, OnDestroy, Output, inject } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MessageService } from 'primeng/api'
import { Subject, debounceTime, takeUntil } from 'rxjs'

import { BugReportPayload, BugReportResult, PendingShot } from '../../data/interfaces/bug-report.interface'
import { WorklogService, worklogErrorMessage } from '../../../worklog/data/services/worklog.service'
import { TraceEvent } from '@app/telemetry/trace-event.interface'
import { buildAutoTitle } from '../../data/utils/auto-title.util'
import { pasteKeyLabel } from '@data/utils/shortcut-label.util'
import { resolveScreen } from '@app/telemetry/screen-labels.constant'
import { TraceService } from '@app/telemetry/trace.service'

const MAX_SHOTS = 4
const MAX_SHOT_BYTES = 5 * 1024 * 1024
const DRAFT_KEY = 'renda-bug-draft'

/**
 * Reportar un problema, en el menor número de pasos posible.
 *
 * Un solo campo obligatorio: qué pasó. El título lo compone el sistema con la pantalla y el último
 * fallo de la bitácora; la captura de la pantalla llega ya adjunta; los últimos pasos van solos.
 *
 * La microcopia está escrita para gente que no sabe de software: quien usa el panel piensa "mi pago
 * no pasó", no en pasos para reproducir ni en resultados esperados. Todo lo que se le pueda quitar
 * de encima, se le quita.
 */
@Component({
  selector: 'app-bug-report-dialog',
  templateUrl: './bug-report-dialog.component.html',
  styleUrls: ['./bug-report-dialog.component.scss'],
})
export class BugReportDialogComponent implements OnDestroy {
  private readonly fb = inject(FormBuilder)
  private readonly worklog = inject(WorklogService)
  private readonly trace = inject(TraceService)
  private readonly messageService = inject(MessageService)
  private readonly destroy$ = new Subject<void>()

  @Input() visible = false
  @Output() visibleChange = new EventEmitter<boolean>()

  form: FormGroup = this.fb.group({
    title: [''],
    description: ['', [Validators.required, Validators.minLength(15)]],
  })

  shots: PendingShot[] = []
  sending = false
  createdCode: string | null = null
  copiado = false
  sendError = ''
  warnings: string[] = []
  showTrace = false
  offline = false
  /** El lanzador está rasterizando la pantalla; se pinta un hueco mientras llega. */
  capturando = false

  /** Los pasos que se van a enviar, ya redactados. Se enseñan en «Ver qué se envía». */
  tracePreview: TraceEvent[] = []

  private screenLabel = ''
  private titleTouched = false
  private clientId = ''

  constructor() {
    //* El título se recalcula mientras se escribe, pero deja de hacerlo en cuanto la persona lo
    //* toca: nada más irritante que un campo que se reescribe solo después de que lo corregiste.
    this.form
      .get('description')!
      .valueChanges.pipe(debounceTime(250), takeUntil(this.destroy$))
      .subscribe(() => this.refreshTitle())

    this.form
      .get('title')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.form.get('title')?.dirty) this.titleTouched = true
      })
  }

  /**
   * Recibe la captura que el lanzador disparó ANTES de abrir este diálogo.
   *
   * Llega tarde a propósito: rasterizar una pantalla con tabla ronda el segundo y medio, y esperarla
   * dejaba a la persona mirando un botón muerto. El hueco se rellena solo cuando la imagen está.
   */
  attachAutoShot(file: File | null): void {
    this.capturando = false
    if (file) this.addShot(file, true)
  }

  /** Prepara el diálogo. Lo llama el lanzador justo después de mostrarlo. */
  prepare(esperandoCaptura = false): void {
    this.capturando = esperandoCaptura
    this.screenLabel = resolveScreen(this.trace.currentUrl).label
    this.tracePreview = this.trace.snapshot()
    this.clientId = this.newId()
    this.offline = navigator.onLine === false
    this.restoreDraft()
    this.refreshTitle()
  }

  get pasteKey(): string {
    return pasteKeyLabel()
  }

  get descriptionInvalid(): boolean {
    const c = this.form.get('description')
    return !!c && c.invalid && (c.dirty || c.touched)
  }

  get canSend(): boolean {
    return this.form.valid && !this.sending
  }

  // ─────────────────────────── Capturas ───────────────────────────

  onPaste(event: ClipboardEvent): void {
    const items = Array.from(event.clipboardData?.items ?? [])
    const images = items.filter((i) => i.kind === 'file' && i.type.startsWith('image/'))
    if (!images.length) return
    //* Solo se corta el pegado cuando de verdad venía una imagen: si no, se rompería el pegado de
    //* texto, que es lo que la gente hace todo el tiempo en este campo.
    event.preventDefault()
    for (const item of images) {
      const file = item.getAsFile()
      if (file) this.addShot(file, false)
    }
  }

  onDrop(event: DragEvent): void {
    //! Sin `preventDefault` el navegador ABRE el archivo y se pierde todo lo escrito.
    event.preventDefault()
    for (const file of Array.from(event.dataTransfer?.files ?? [])) this.addShot(file, false)
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault()
  }

  onFilesChosen(event: Event): void {
    const input = event.target as HTMLInputElement
    for (const file of Array.from(input.files ?? [])) this.addShot(file, false)
    input.value = ''
  }

  removeShot(shot: PendingShot): void {
    URL.revokeObjectURL(shot.preview)
    this.shots = this.shots.filter((s) => s !== shot)
  }

  private addShot(file: File, automatic: boolean): void {
    if (this.shots.length >= MAX_SHOTS) {
      this.toast('warn', 'Ya son suficientes', `Se pueden adjuntar hasta ${MAX_SHOTS} capturas.`)
      return
    }
    if (!file.type.startsWith('image/')) {
      this.toast('warn', 'No se pudo adjuntar', 'Solo se pueden adjuntar imágenes.')
      return
    }
    if (file.size > MAX_SHOT_BYTES) {
      this.toast('warn', 'La imagen pesa demasiado', 'Cada captura debe pesar menos de 5 MB.')
      return
    }
    this.shots = [...this.shots, { file, preview: URL.createObjectURL(file), automatic }]
  }

  // ─────────────────────────── Envío ───────────────────────────

  submit(): void {
    this.form.markAllAsTouched()
    if (!this.canSend) return

    if (navigator.onLine === false) {
      this.saveDraft()
      this.offline = true
      this.sendError = 'Parece que no hay internet. Guardamos lo que escribiste y podrás enviarlo en cuanto vuelva la conexión.'
      return
    }

    this.sending = true
    this.sendError = ''

    const payload: BugReportPayload = {
      clientId: this.clientId,
      title: (this.form.value.title || '').trim(),
      description: (this.form.value.description || '').trim(),
      route: this.trace.currentScreen,
      screen: this.screenLabel,
      trace: this.trace.payload(),
      shots: this.shots.map((s) => s.file),
    }

    this.worklog.reportBug(payload).subscribe({
      next: (result: BugReportResult | null) => {
        this.sending = false
        this.createdCode = result?.item?.code ?? null
        this.warnings = result?.warnings ?? []
        this.clearDraft()
        //* La bitácora se vacía al enviarla: los pasos ya viajaron con el reporte, y conservarlos
        //* haría que el siguiente reporte arrastrara el problema anterior.
        this.trace.clear('sent')
      },
      error: (err) => {
        this.sending = false
        //* El formulario NO se pierde. Se guarda como borrador y se le ofrece reintentar.
        this.saveDraft()
        this.sendError = worklogErrorMessage(err, 'No pudimos enviar tu reporte. Vuelve a intentarlo en un momento.')
      },
    })
  }

  copyCode(): void {
    if (!this.createdCode) return
    navigator.clipboard.writeText(this.createdCode)
    this.copiado = true
    setTimeout(() => (this.copiado = false), 2000)
  }

  otro(): void {
    this.reset()
    this.prepare()
  }

  close(): void {
    this.visible = false
    this.visibleChange.emit(false)
    //* Se limpia al CERRAR y no al abrir: limpiar al abrir dejaría ver por un instante el folio del
    //* reporte anterior.
    this.reset()
  }

  // ─────────────────────────── Borrador ───────────────────────────

  /**
   * Guarda lo escrito para no perderlo si falla el envío o no hay red.
   *
   * Las capturas NO se guardan: un `File` no se serializa, y meterlo en base64 reventaría la cuota
   * del almacenamiento. Sobrevive a una recarga, no al cierre de la pestaña.
   */
  private saveDraft(): void {
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ ...this.form.value, clientId: this.clientId, savedAt: Date.now() }))
    } catch {
      //* Sin almacenamiento, el texto sigue en pantalla mientras el diálogo esté abierto.
    }
  }

  private restoreDraft(): void {
    try {
      const raw = sessionStorage.getItem(DRAFT_KEY)
      if (!raw) return
      const draft = JSON.parse(raw)
      if (!draft?.description) return
      this.form.patchValue({ title: draft.title ?? '', description: draft.description })
      //* Se conserva el identificador del intento anterior: si aquel envío sí llegó, el servidor
      //* puede reconocerlo y no crear un reporte duplicado.
      if (draft.clientId) this.clientId = draft.clientId
      this.titleTouched = !!draft.title
    } catch {
      this.clearDraft()
    }
  }

  private clearDraft(): void {
    try {
      sessionStorage.removeItem(DRAFT_KEY)
    } catch {
      //* Nada que limpiar.
    }
  }

  // ─────────────────────────── Interno ───────────────────────────

  private refreshTitle(): void {
    if (this.titleTouched) return
    const title = buildAutoTitle({
      screenLabel: this.screenLabel,
      description: this.form.get('description')?.value ?? '',
      lastFailure: this.trace.lastFailure(),
    })
    this.form.get('title')?.setValue(title, { emitEvent: false })
  }

  private reset(): void {
    for (const shot of this.shots) URL.revokeObjectURL(shot.preview)
    this.shots = []
    this.form.reset({ title: '', description: '' })
    this.titleTouched = false
    this.createdCode = null
    this.sendError = ''
    this.warnings = []
    this.showTrace = false
    this.copiado = false
    this.capturando = false
  }

  private toast(severity: string, summary: string, detail: string): void {
    this.messageService.add({ key: 'bugreport', severity, summary, detail, life: 4000 })
  }

  private newId(): string {
    try {
      return crypto.randomUUID()
    } catch {
      return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
    for (const shot of this.shots) URL.revokeObjectURL(shot.preview)
  }
}
